import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';

import usePartChannelPostMutation from '../inventory/usePartChannelPostMutation';


/**
 * Sales mutation hooks (T10). Each wraps the shared `useMutation` engine with the
 * injected-`queryClient` `mutateFn({ apiClient, queryClient })` contract — the
 * corelib query singleton is NOT imported here. Invalidation runs inside
 * `mutateFn` (the one place `queryClient` is injected) so the exact cache key
 * prefixes are preserved unchanged.
 *
 * Mirrors `query/garage/useDonorMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 *
 * Two invalidation surfaces, kept exactly as the inline handlers had them:
 *  - Sale write (record / edit / manual / delete) touches BOTH the sales list
 *    AND the inventory caches (a sale flips part stock / listing state /
 *    dashboard totals) — see `invalidateSale`.
 *  - Mark-sold / cleanup invalidates ONLY the inventory caches (the Sales page
 *    refetches its own list/totals via the modal `onDone`/`onSuccess` callbacks),
 *    matching the original `useMarkSold` controller — see `invalidateInventory`.
 */

/** Cache key prefix for a seller's sales list/totals/export. */
function salesKeyPrefix(sellerId) {
  return queryKeyPrefix('sales', String(sellerId || ''));
}

/** Cross-resource inventory key prefix for a seller's `parts`/`listings`/`dashboard`. */
function inventoryKeyPrefix(sellerId, resource) {
  return queryKeyPrefix('inventory', resource, String(sellerId || ''));
}

/** Invalidate the inventory caches a sale touches (part stock, listings, dashboard). */
function invalidateInventory(queryClient, sellerId) {
  for (const resource of ['parts', 'listings', 'dashboard']) {
    queryClient.invalidateQueries({ queryKey: inventoryKeyPrefix(sellerId, resource) });
  }
}

/** Invalidate the sales list + the cross-resource inventory caches (a full sale write). */
function invalidateSale(queryClient, sellerId) {
  queryClient.invalidateQueries({ queryKey: salesKeyPrefix(sellerId) });
  invalidateInventory(queryClient, sellerId);
}


/**
 * Mark a part sold (Step 1, record mode). Variables: `{ sellerId, partId, payload }`.
 * POSTs the mark-sold endpoint and returns the response (`{ sale, part_status,
 * other_live_channels }`). Invalidation is the caller's responsibility via
 * `onDone` (matching the original `useMarkSold` flow), so NO invalidation runs
 * here — keeping the record path and the controller's `invalidate()` separate.
 */
function useMarkSoldMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, partId, payload }) => apiClient.request({
      url: `/sales/${sellerId}/parts/${partId}/mark-sold/`,
      method: 'POST',
      payload,
    }),
    ...params,
  });
}


/**
 * Persist Step-2 cleanup removals for a recorded sale. Variables:
 * `{ sellerId, saleId, channels }`. POSTs the cleanup endpoint. No invalidation
 * (the controller's `onDone` handles inventory cache refresh).
 */
function useSaleCleanupMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, saleId, channels }) => apiClient.request({
      url: `/sales/${sellerId}/${saleId}/cleanup/`,
      method: 'POST',
      payload: { channels },
    }),
    ...params,
  });
}


/**
 * Cleanup-only path (no Sale id): toggle a single channel off for a part via the
 * channel endpoint. Variables: `{ sellerId, partId, channel }`. No invalidation
 * (the controller's `onDone` handles inventory cache refresh).
 */
function usePartChannelOffMutation(params) {
  return usePartChannelPostMutation({
    buildPayload: ({ channel }) => ({ channel, live: false }),
  }, params);
}


/**
 * Edit a sale's soft fields (#300). Variables: `{ sellerId, saleId, payload }`.
 * PATCHes only the changed fields (the caller builds the partial payload), then
 * invalidates the sales + inventory caches. The backend strips `price` from the
 * response for financially-blind roles — this hook just relays the request /
 * response, so that gating is untouched.
 */
function useSaleEditMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, saleId, payload }) => {
      const data = await apiClient.request({
        url: `/sales/${sellerId}/${saleId}/`,
        method: 'PATCH',
        payload,
      });
      invalidateSale(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Create a part-less manual revenue Sale (#139). Variables: `{ sellerId, payload }`.
 * POSTs the manual endpoint, then invalidates the sales + inventory caches.
 */
function useManualRevenueMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, payload }) => {
      const data = await apiClient.request({
        url: `/sales/${sellerId}/manual/`,
        method: 'POST',
        payload,
      });
      invalidateSale(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Hard-delete a part-less manual revenue row (#300). Variables: `{ sellerId, saleId }`.
 * DELETEs the sale, then invalidates the sales + inventory caches. The server
 * enforces the part-bound guard regardless of the UI (a 400 surfaces to
 * `onError`).
 */
function useSaleDeleteMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, saleId }) => {
      const data = await apiClient.request({
        url: `/sales/${sellerId}/${saleId}/`,
        method: 'DELETE',
      });
      invalidateSale(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Export the current sales view as CSV. Variables: `{ sellerId, params }` where
 * `params` are the shared list query params. Returns the raw CSV text; the
 * caller turns it into a Blob download. No cache invalidation (read-only).
 */
function useSalesExportMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, params: queryParams }) => apiClient.request({
      url: `/sales/${sellerId}/export/`,
      payload: queryParams,
    }),
    ...params,
  });
}


export {
  invalidateInventory,
  inventoryKeyPrefix,
  salesKeyPrefix,
  useManualRevenueMutation,
  useMarkSoldMutation,
  usePartChannelOffMutation,
  useSaleCleanupMutation,
  useSaleDeleteMutation,
  useSaleEditMutation,
  useSalesExportMutation,
};
